import React, {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import NoSleep from "nosleep.js";
import * as signalR from "@microsoft/signalr";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, Tooltip } from "@mui/material";
import { IconButton, Avatar } from "@mui/material";
import Send from "@mui/icons-material/Send";
import DoneAll from "@mui/icons-material/DoneAll";
import Schedule from "@mui/icons-material/Schedule";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import History from "@mui/icons-material/History";
import Reply from "@mui/icons-material/Reply";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import DeleteSweep from "@mui/icons-material/DeleteSweep";
import EditOutlined from "@mui/icons-material/EditOutlined";
import Check from "@mui/icons-material/Check";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Pause from "@mui/icons-material/Pause";
import {
  faChevronRight,
  faCamera,
  faPaperclip,
  faFilePdf,
  faMicrophone,
  faRotateRight,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import "./ChatPage.css";
import "../../Components/Reviews/Reviews.css";
import ReviewAction from "../../Components/Reviews/ReviewAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CloseConsultation,
  GetInitialChatInfo,
  GetPreviousConsultationMessages,
  ReopenConsultation,
} from "../../Services/ConsultionService";
import Loader from "../../Components/Core/Loader";
import { CheckRole } from "../../Services/AuthService";
import ConfirmModal from "../../Components/Modals/ConfirmModal";
import InfoModal from "../../Components/Modals/InfoModal";
import MicRecorder from "mic-recorder-to-mp3";
import { getDecodedUserId } from "../../Services/GeneralService";
import { GetUploadPermissions } from "../../Services/SettingService";
import { useTenantContent } from "../../Context/TenantContentContext";
import { resolveMediaUrl } from "../../utils/seo";

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const baseUrl = process.env.REACT_APP_BASE_URL;

const LINK_PATTERN = /(https?:\/\/[^\s<>"'،]+|www\.[^\s<>"'،]+)/gi;

const toHref = (raw) => {
  const trimmed = raw.replace(/[.,;:!?،)\]}'"]+$/g, "");
  const trailing = raw.slice(trimmed.length);
  const href = /^www\./i.test(trimmed) ? `https://${trimmed}` : trimmed;
  return { href, label: trimmed, trailing };
};

const renderLinkedText = (text) => {
  if (!text) return null;
  const parts = String(text).split(LINK_PATTERN);
  return parts.map((part, index) => {
    if (!part) return null;
    if (!/^(https?:\/\/|www\.)/i.test(part)) {
      return part;
    }
    const { href, label, trailing } = toHref(part);
    return (
      <React.Fragment key={`${href}-${index}`}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-link"
          onClick={(e) => e.stopPropagation()}
        >
          {label}
        </a>
        {trailing}
      </React.Fragment>
    );
  });
};

const openInNewTab = (url, event) => {
  event?.preventDefault();
  event?.stopPropagation();
  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

const imageFileName = (url) => {
  if (!url || url.startsWith("blob:") || url.startsWith("data:")) {
    return `image-${Date.now()}.jpg`;
  }
  try {
    const name = decodeURIComponent(
      new URL(url, window.location.href).pathname.split("/").pop() || "",
    );
    if (/\.(jpe?g|png|webp|gif)$/i.test(name)) return name;
  } catch {
    /* keep the fallback name */
  }
  return `image-${Date.now()}.jpg`;
};

const chatImageDownload = (url) => {
  if (!url) return null;
  const fileName = imageFileName(url);
  if (url.startsWith("blob:") || url.startsWith("data:")) {
    return { href: url, fileName, direct: true };
  }
  return {
    href: `/api/Consultation/download-image?url=${encodeURIComponent(url)}`,
    fileName,
    direct: false,
  };
};

const formatAudioTime = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const ChatAudioPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const readDuration = () => {
    const d = audioRef.current?.duration;
    if (d && Number.isFinite(d) && d > 0) setDuration(d);
  };

  const toggle = (e) => {
    e.stopPropagation();
    const el = audioRef.current;
    if (!el) return;
    document.querySelectorAll(".chat-audio-el").forEach((node) => {
      if (node !== el) node.pause();
    });
    if (el.paused) {
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  };

  const seek = (e) => {
    e.stopPropagation();
    const el = audioRef.current;
    const value = Number(e.target.value);
    if (!el || !Number.isFinite(value)) return;
    el.currentTime = value;
    setCurrent(value);
  };

  const timeLabel = playing || current > 0 ? current : duration;
  const rangeMax = duration > 0 ? duration : 1;
  const pct = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;

  return (
    <div
      className="chat-audio-player"
      dir="ltr"
      onClick={(e) => e.stopPropagation()}
    >
      <audio
        ref={audioRef}
        className="chat-audio-el"
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
        }}
        onTimeUpdate={() => setCurrent(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={readDuration}
        onDurationChange={readDuration}
      />
      <button type="button" className="chat-audio-play" onClick={toggle}>
        {playing ? <Pause /> : <PlayArrow />}
      </button>
      <div className="chat-audio-rail">
        <span className="chat-audio-fill" style={{ width: `${pct}%` }} />
        <input
          type="range"
          className="chat-audio-track"
          min={0}
          max={rangeMax}
          step="0.01"
          value={Math.min(current, rangeMax)}
          onChange={seek}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <span className="chat-audio-time">{formatAudioTime(timeLabel)}</span>
    </div>
  );
};

const clampGalleryScale = (value) => Math.min(4, Math.max(1, value));

const ChatZoomableImage = ({ src, allowSwipe, onSwipe }) => {
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const motion = useRef({
    scale: 1,
    x: 0,
    y: 0,
    pointers: new Map(),
    pinch: null,
    pan: null,
    swipeX: null,
    didPinch: false,
    lastTapAt: 0,
    lastTapX: 0,
    lastTapY: 0,
  });

  const applyTransform = () => {
    const img = imgRef.current;
    if (!img) return;
    const { x, y, scale } = motion.current;
    img.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  };

  const resetTransform = () => {
    motion.current.scale = 1;
    motion.current.x = 0;
    motion.current.y = 0;
    applyTransform();
  };

  const originFromClient = (clientX, clientY) => {
    const rect = stageRef.current.getBoundingClientRect();
    return {
      x: clientX - (rect.left + rect.width / 2),
      y: clientY - (rect.top + rect.height / 2),
    };
  };

  const zoomAt = (nextScale, clientX, clientY) => {
    const state = motion.current;
    const scale = clampGalleryScale(nextScale);
    const origin = originFromClient(clientX, clientY);
    const ratio = scale / state.scale;
    state.x = origin.x - ratio * (origin.x - state.x);
    state.y = origin.y - ratio * (origin.y - state.y);
    state.scale = scale;
    if (state.scale <= 1.02) {
      state.scale = 1;
      state.x = 0;
      state.y = 0;
    }
    applyTransform();
  };

  useEffect(() => {
    resetTransform();
  }, [src]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    const pointerValues = () => [...motion.current.pointers.values()];
    const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

    const onPointerDown = (e) => {
      const state = motion.current;
      state.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (state.pointers.size >= 2) {
        state.didPinch = true;
        state.swipeX = null;
        state.pan = null;
        const [a, b] = pointerValues();
        state.pinch = {
          dist: Math.max(distance(a, b), 1),
          scale: state.scale,
        };
        return;
      }

      if (state.scale > 1.02) {
        state.pan = {
          x: state.x,
          y: state.y,
          px: e.clientX,
          py: e.clientY,
        };
      } else if (allowSwipe) {
        state.swipeX = e.clientX;
        state.didPinch = false;
      }
    };

    const onPointerMove = (e) => {
      const state = motion.current;
      if (!state.pointers.has(e.pointerId)) return;
      state.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (state.pointers.size >= 2 && state.pinch) {
        e.preventDefault();
        const [a, b] = pointerValues();
        const dist = Math.max(distance(a, b), 1);
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        zoomAt(state.pinch.scale * (dist / state.pinch.dist), midX, midY);
        return;
      }

      if (state.pan && state.pointers.size === 1) {
        e.preventDefault();
        state.x = state.pan.x + (e.clientX - state.pan.px);
        state.y = state.pan.y + (e.clientY - state.pan.py);
        applyTransform();
      }
    };

    const onPointerUp = (e) => {
      const state = motion.current;
      state.pointers.delete(e.pointerId);

      if (state.pointers.size < 2) state.pinch = null;
      if (state.pointers.size === 1 && state.scale > 1.02) {
        const p = pointerValues()[0];
        state.pan = { x: state.x, y: state.y, px: p.x, py: p.y };
      } else if (state.pointers.size === 0) {
        state.pan = null;
        if (state.scale < 1.05) {
          state.scale = 1;
          state.x = 0;
          state.y = 0;
          applyTransform();
        }

        const now = Date.now();
        const tapDist = Math.hypot(
          e.clientX - state.lastTapX,
          e.clientY - state.lastTapY,
        );
        if (!state.didPinch && now - state.lastTapAt < 280 && tapDist < 28) {
          if (state.scale > 1.2) resetTransform();
          else zoomAt(2.4, e.clientX, e.clientY);
          state.lastTapAt = 0;
        } else {
          state.lastTapAt = now;
          state.lastTapX = e.clientX;
          state.lastTapY = e.clientY;
        }

        if (
          allowSwipe &&
          !state.didPinch &&
          state.scale <= 1.02 &&
          state.swipeX != null
        ) {
          const dx = e.clientX - state.swipeX;
          if (Math.abs(dx) >= 50) onSwipe?.(dx);
        }
        state.swipeX = null;
        state.didPinch = false;
      }
    };

    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 0.89;
      zoomAt(motion.current.scale * factor, e.clientX, e.clientY);
    };

    const onTouchMove = (e) => {
      if (e.touches.length >= 2 || motion.current.scale > 1.02) {
        e.preventDefault();
      }
    };

    stage.addEventListener("pointerdown", onPointerDown);
    stage.addEventListener("pointermove", onPointerMove, { passive: false });
    stage.addEventListener("pointerup", onPointerUp);
    stage.addEventListener("pointercancel", onPointerUp);
    stage.addEventListener("wheel", onWheel, { passive: false });
    stage.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      stage.removeEventListener("pointerdown", onPointerDown);
      stage.removeEventListener("pointermove", onPointerMove);
      stage.removeEventListener("pointerup", onPointerUp);
      stage.removeEventListener("pointercancel", onPointerUp);
      stage.removeEventListener("wheel", onWheel);
      stage.removeEventListener("touchmove", onTouchMove);
    };
  }, [allowSwipe, onSwipe, src]);

  return (
    <div ref={stageRef} className="chat-gallery-stage">
      <img ref={imgRef} src={src} alt="تصویر تمام‌صفحه" draggable={false} />
    </div>
  );
};

const ChatComponent = () => {
  const { content, loading: load } = useTenantContent();

  const navigate = useNavigate();
  const { sessionId: sessionIdParam } = useParams();
  const sessionId = parseInt(sessionIdParam, 10);
  const userId = getDecodedUserId();

  const [connection, setConnection] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyToMessageId, setReplyToMessageId] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [session, setSession] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploadPermissions, setUploadPermissions] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogForReopenConsultation, setOpenDialogForReopenConsultation] =
    useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isChatEnded, setIsChatEnded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [deleteMessageIds, setDeleteMessageIds] = useState([]);
  const [highlightedId, setHighlightedId] = useState(null);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [hasPreviousMessages, setHasPreviousMessages] = useState(false);
  const [showHistoryHint, setShowHistoryHint] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const [previousGroups, setPreviousGroups] = useState([]);
  const [previousLoading, setPreviousLoading] = useState(false);
  const [previousError, setPreviousError] = useState("");

  const [attachOpen, setAttachOpen] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState(null);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [recording, setRecording] = useState(false);
  const imageInputRef = useRef(null);
  const composerRef = useRef(null);

  const isDoctor = CheckRole(["Doctor"]);
  const isPatient = !isDoctor;

  // ✅ تغییر: حالا uploadPermissions همیشه یک object است
  const canUploadImage = isDoctor || uploadPermissions?.allowPatientImageUpload;
  const canUploadPdf = isDoctor || uploadPermissions?.allowPatientPdfUpload;
  const canUploadVoice = isDoctor || uploadPermissions?.allowPatientVoiceUpload;

  // Ref for wake lock and no sleep
  const wakeLockSentinel = useRef(null);
  const noSleep = useRef(null);

  const chatRootRef = useRef(null);

  // Chat list scroll: pin to bottom only when the user is already near the end.
  const messagesContainerRef = useRef(null);
  const messagesInnerRef = useRef(null);
  const bottomSentinelRef = useRef(null);
  const isPinnedToBottomRef = useRef(true);
  const forceScrollRef = useRef(true);
  const prevLastMessageIdRef = useRef(null);
  const messageRefs = useRef({});
  const fileInputRef = useRef(null);
  const sessionRef = useRef(null);
  const formatIncomingMessageRef = useRef(null);
  const chatLoadIdRef = useRef(0);
  const pendingHistoryScrollRef = useRef(null);
  const JUMP_BUTTON_PX = 1;
  const NEAR_BOTTOM_PX = 2;

  const location = useLocation();
  const from = location.state?.from || "/counseling/chat/index";

  // ====
  const status = location.state?.status || "active";

  const infoModalStorageKey = `chatGuideShown_${sessionId}_${userId}`;
  const historyHintStorageKey = `chatHistoryHintShown_${userId}`;

  const handleInfoModalClose = () => {
    setOpenInfoModal(false);
    localStorage.setItem(infoModalStorageKey, "true");
  };

  const handleFinishConsultation = () => {
    setOpenDialog(true);
  };

  const handleReopenConsultation = () => {
    setOpenDialogForReopenConsultation(true);
  };

  const allImages = React.useMemo(() => {
    const history = showPrevious
      ? previousGroups.flatMap((group) => group.messages || [])
      : [];
    return [...history, ...messages].filter(
      (m) => m.type === "photo" && !m.isPlaceholder,
    );
  }, [messages, previousGroups, showPrevious]);

  const openFullScreenImage = (clickedMessageId) => {
    const clickedIndex = allImages.findIndex((m) => m.id === clickedMessageId);
    if (clickedIndex !== -1) setCurrentImageIndex(clickedIndex);
  };

  const closeGallery = () => {
    setCurrentImageIndex(null);
  };

  const currentImageDownload = chatImageDownload(
    currentImageIndex != null ? allImages[currentImageIndex]?.photoUrl : "",
  );

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => {
      if (prevIndex == null || prevIndex >= allImages.length - 1) {
        return prevIndex;
      }
      return prevIndex + 1;
    });
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) => {
      if (prevIndex == null || prevIndex <= 0) return prevIndex;
      return prevIndex - 1;
    });
  };

  const handleGallerySwipe = useCallback(
    (dx) => {
      setCurrentImageIndex((prevIndex) => {
        if (prevIndex == null) return prevIndex;
        if (dx < 0) {
          return prevIndex >= allImages.length - 1 ? prevIndex : prevIndex + 1;
        }
        return prevIndex <= 0 ? prevIndex : prevIndex - 1;
      });
    },
    [allImages.length],
  );

  useEffect(() => {
    if (currentImageIndex == null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") handlePrevImage();
      else if (e.key === "ArrowRight") handleNextImage();
      else if (e.key === "Escape") closeGallery();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [currentImageIndex, allImages.length]);

  useEffect(() => {
    if (currentImageIndex == null) return;
    if (allImages.length === 0) {
      setCurrentImageIndex(null);
      return;
    }
    if (currentImageIndex >= allImages.length) {
      setCurrentImageIndex(allImages.length - 1);
    }
  }, [allImages.length, currentImageIndex]);

  const getDistanceFromBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return 0;
    return el.scrollHeight - el.scrollTop - el.clientHeight;
  }, []);

  const scrollingProgrammaticallyRef = useRef(false);

  const scrollChatToBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    scrollingProgrammaticallyRef.current = true;
    el.scrollTop = el.scrollHeight;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
      scrollingProgrammaticallyRef.current = false;
    });
  }, []);

  const syncJumpButton = useCallback(() => {
    if (scrollingProgrammaticallyRef.current) return;
    const distance = getDistanceFromBottom();
    const atBottom = distance <= NEAR_BOTTOM_PX;
    isPinnedToBottomRef.current = atBottom;
    setShowJumpToLatest(distance > JUMP_BUTTON_PX);
  }, [getDistanceFromBottom]);

  const handleMessagesScroll = () => {
    syncJumpButton();
  };

  const scrollToMessage = (id) => {
    const node = messageRefs.current[id];
    const container = messagesContainerRef.current;
    if (!node || !container) return;

    const nodeRect = node.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const offset = nodeRect.top - containerRect.top + container.scrollTop;
    container.scrollTo({
      top: offset - container.clientHeight / 2 + node.offsetHeight / 2,
      behavior: "smooth",
    });
    isPinnedToBottomRef.current = false;
    setShowJumpToLatest(true);
    setHighlightedId(id);
    setTimeout(() => setHighlightedId(null), 2000);
  };

  // To this (wrapped in useCallback):
  const formatIncomingMessage = useCallback(
    (raw) => {
      const currentSession = sessionRef.current;
      if (!raw || !currentSession) return null;

      const id = raw.id ?? raw.Id;
      const senderId = raw.senderId ?? raw.SenderId;
      const messageType = raw.messageType ?? raw.MessageType;
      const content = raw.content ?? raw.Content;
      const timestamp = raw.timestamp ?? raw.Timestamp;
      const replyToMessageId =
        raw.replyToMessageId ?? raw.ReplyToMessageId ?? null;

      if (id == null || senderId == null) return null;

      const position = senderId === userId ? "right" : "left";
      const title =
        senderId === userId
          ? "شما"
          : senderId === currentSession.doctorId
            ? currentSession.doctorName
            : currentSession.patientName;
      const base = {
        id,
        position,
        title,
        date: new Date(timestamp),
        isEdited: !!(raw.isEdited ?? raw.IsEdited),
        isDeleted: false,
        status: senderId === userId ? "sent" : "",
        showRemoveButton: senderId === userId && currentSession.status === 0,
        senderId,
        replyToMessageId: replyToMessageId || null,
      };

      switch (messageType) {
        case "photo":
          return { ...base, type: "photo", photoUrl: content };
        case "file":
          return { ...base, type: "file", fileUrl: content };
        case "audio":
          return { ...base, type: "audio", audioUrl: content };
        default:
          return { ...base, type: "text", text: content };
      }
    },
    [userId],
  );

  formatIncomingMessageRef.current = formatIncomingMessage;

  const formatStoredMessage = useCallback(
    (raw, isPrevious = false) => {
      const currentSession = sessionRef.current;
      if (!raw || !currentSession) return null;

      const id = raw.id ?? raw.Id;
      const senderId = raw.senderId ?? raw.SenderId;
      const messageType = raw.messageType ?? raw.MessageType;
      const content = raw.content ?? raw.Content;
      const timestamp = raw.timestamp ?? raw.Timestamp;
      const isRead = raw.isRead ?? raw.IsRead;
      const replyToMessageId =
        raw.replyToMessageId ?? raw.ReplyToMessageId ?? null;

      if (id == null || senderId == null) return null;

      const title =
        senderId === userId
          ? "شما"
          : senderId === currentSession.doctorId
            ? currentSession.doctorName
            : currentSession.patientName;

      const base = {
        id,
        position: senderId === userId ? "right" : "left",
        title,
        date: new Date(timestamp),
        status: senderId === userId ? (isRead ? "read" : "sent") : "",
        showRemoveButton:
          !isPrevious && senderId === userId && currentSession.status === 0,
        senderId,
        replyToMessageId: replyToMessageId || null,
        isEdited: !!(raw.isEdited ?? raw.IsEdited),
        isPrevious,
      };

      switch (messageType) {
        case "photo":
          return { ...base, type: "photo", photoUrl: content };
        case "file":
          return { ...base, type: "file", fileUrl: content };
        case "audio":
          return { ...base, type: "audio", audioUrl: content };
        default:
          return { ...base, type: "text", text: content };
      }
    },
    [userId],
  );

  // Helper function to request the screen lock
  const requestWakeLock = async () => {
    // Modern API for Chrome, Edge, etc.
    if ("wakeLock" in navigator) {
      try {
        wakeLockSentinel.current = await navigator.wakeLock.request("screen");
        console.log("Screen Wake Lock is active.");
      } catch (err) {
        console.error(`Wake Lock request failed: ${err.name}, ${err.message}`);
      }
    } else {
      // Fallback for Safari and other browsers
      console.log("Using NoSleep.js fallback.");
      if (!noSleep.current) {
        noSleep.current = new NoSleep();
      }
      // A user interaction (like clicking "record") is required to enable it
      noSleep.current.enable();
    }
  };

  // Helper function to release the screen lock
  const releaseWakeLock = async () => {
    if (wakeLockSentinel.current) {
      // For modern API
      await wakeLockSentinel.current.release();
      wakeLockSentinel.current = null;
      console.log("Screen Wake Lock released.");
    } else if (noSleep.current) {
      // For the fallback
      console.log("Disabling NoSleep.js.");
      noSleep.current.disable();
    }
  };

  const GetChatInfo = useCallback(
    async ({ keepLoading } = {}) => {
      const loadId = ++chatLoadIdRef.current;
      setLoading(true);
      forceScrollRef.current = true;
      isPinnedToBottomRef.current = true;
      setShowPrevious(false);
      setPreviousGroups([]);
      setPreviousError("");
      try {
        const { messages, session, hasPreviousMessages: hasPrevious } =
          await GetInitialChatInfo(sessionId);
        if (loadId !== chatLoadIdRef.current) return;

        // ✅ دریافت تنظیمات آپلود
        const permissions = await GetUploadPermissions();
        if (loadId !== chatLoadIdRef.current) return;
        setUploadPermissions(permissions);

        sessionRef.current = session;
        const formattedMessages = messages
          .map((message) => formatStoredMessage(message, false))
          .filter(Boolean);

        setMessages(formattedMessages);
        setSession(session);
        setHasPreviousMessages(!!hasPrevious);
        setIsChatEnded(session.status !== 0);
      } catch (error) {
        console.error("Error loading chat info:", error);
      } finally {
        if (!keepLoading && loadId === chatLoadIdRef.current) {
          setLoading(false);
        }
      }
    },
    [sessionId, formatStoredMessage],
  );

  const handleAttachClick = () => setAttachOpen((open) => !open);
  const handleAttachClose = () => setAttachOpen(false);

  const handleOptimisticFileUpload = useCallback(
    async (fileOrBlob, messageType, uploadEndpoint) => {
      // 1. Create a local URL for instant preview and a temporary ID.
      const localUrl = URL.createObjectURL(fileOrBlob);
      const tempId = `temp_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      const messageTypeProps = {
        photo: { type: "photo", photoUrl: localUrl, data: { uri: localUrl } },
        audio: {
          type: "audio",
          audioUrl: localUrl,
          data: { audioURL: localUrl },
        },
        file: { type: "file", fileUrl: localUrl, data: { uri: localUrl } },
      };

      const optimisticMessage = {
        id: tempId,
        position: "right",
        title: "شما",
        date: new Date(),
        status: "waiting",
        senderId: userId,
        showRemoveButton: true,
        replyToMessageId: replyToMessageId,
        retryPayload: {
          fileOrBlob,
          messageType,
          uploadEndpoint,
          localUrl,
        },
        ...messageTypeProps[messageType],
      };

      forceScrollRef.current = true;
      isPinnedToBottomRef.current = true;
      setMessages((prev) => [...prev, optimisticMessage]);
      setReplyToMessageId(null);
      setReplyToMessage(null);

      try {
        const formData = new FormData();
        const fileExtension = fileOrBlob.name
          ? fileOrBlob.name.split(".").pop()
          : "mp3";
        formData.append("file", fileOrBlob, `upload.${fileExtension}`);

        const response = await fetch(`${baseUrl}${uploadEndpoint}`, {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok)
          throw new Error(`Upload failed with status: ${response.status}`);

        const responseData = await response.json();

        const permanentUrl = responseData.imageUrl || responseData.fileUrl;

        const replyId = optimisticMessage.replyToMessageId;
        const permanentMessageData = await connection.invoke(
          "SendMessage",
          sessionId,
          userId,
          messageType,
          permanentUrl,
          replyId,
        );

        const formatted = formatIncomingMessage(permanentMessageData);
        if (!formatted) {
          throw new Error("Could not format uploaded message");
        }

        setMessages((prev) => {
          if (prev.some((m) => m.id === formatted.id)) {
            return prev.filter((m) => m.id !== tempId);
          }
          return prev.map((msg) =>
            msg.id === tempId ? { ...formatted, status: "sent" } : msg,
          );
        });

        URL.revokeObjectURL(localUrl);
      } catch (error) {
        console.error(`${messageType} Upload Error:`, error);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId ? { ...msg, status: "failed" } : msg,
          ),
        );
      }
    },
    [connection, sessionId, userId, replyToMessageId, formatIncomingMessage],
  );

  // Image
  const onImageSelect = () => {
    handleAttachClose();
    imageInputRef.current.click();
  };

  //  File (PDF/DOC)
  const onFileSelect = () => {
    handleAttachClose();
    fileInputRef.current.click();
  };

  const onVoiceSelect = async () => {
    if (!recording) {
      try {
        await requestWakeLock();
        await Mp3Recorder.start();
        setRecording(true);
      } catch (err) {
        await releaseWakeLock();
        console.error("Recording failed to start:", err);
        alert("لطفاً دسترسی به میکروفون را فعال کنید.");
      }
    } else {
      setRecording(false);
      handleAttachClose();
      await releaseWakeLock();
      try {
        const [, blob] = await Mp3Recorder.stop().getMp3();
        handleOptimisticFileUpload(blob, "audio", "/Consultation/upload-voice");
      } catch (e) {
        console.error("Could not process audio:", e);
        alert("خطا در ضبط و ارسال صدا");
      }
    }
  };

  const handleImageUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    for (const file of selectedFiles) {
      await handleOptimisticFileUpload(
        file,
        "photo",
        "/Consultation/upload-image",
      );
    }

    // Allows selecting the exact same files again if needed
    e.target.value = "";
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      handleOptimisticFileUpload(file, "file", "/Consultation/upload-file");
    }
  };

  useEffect(() => {
    document.documentElement.classList.add("chat-page-active");

    return () => {
      document.documentElement.classList.remove("chat-page-active");
    };
  }, []);

  // On-screen keyboards shrink the visual viewport, not the layout viewport, so a
  // full-screen fixed layout either hides the composer behind the keyboard or leaves
  // a gap above it. Size the chat to the visual viewport instead.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    let frame = 0;
    let lastHeight = 0;
    let lastTop = 0;

    const sync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const root = chatRootRef.current;
        if (!root) return;

        const height = Math.round(viewport.height);
        const top = Math.round(viewport.offsetTop);

        // Rewriting identical values on every keyboard-animation frame is what
        // makes the layout visibly jitter.
        if (height === lastHeight && top === lastTop) return;
        lastHeight = height;
        lastTop = top;

        // Shrinking the container makes the browser clamp scrollTop, which would
        // otherwise flash the jump-to-latest button mid-resize.
        scrollingProgrammaticallyRef.current = true;

        root.style.height = `${height}px`;
        root.style.top = `${top}px`;

        if (isPinnedToBottomRef.current) {
          const thread = messagesContainerRef.current;
          if (thread) thread.scrollTop = thread.scrollHeight;
        }

        requestAnimationFrame(() => {
          scrollingProgrammaticallyRef.current = false;
        });
      });
    };

    if (window.scrollY !== 0) window.scrollTo(0, 0);
    sync();
    viewport.addEventListener("resize", sync);
    viewport.addEventListener("scroll", sync);
    window.addEventListener("orientationchange", sync);

    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", sync);
      viewport.removeEventListener("scroll", sync);
      window.removeEventListener("orientationchange", sync);
      const root = chatRootRef.current;
      if (root) {
        root.style.height = "";
        root.style.top = "";
      }
    };
  }, [load, content, loading]);

  useEffect(() => {
    document.title = "مشاوره آنلاین";
    let cancelled = false;
    let hubConnection = null;

    const initializeChat = async () => {
      try {
        setLoading(true);
        await GetChatInfo({ keepLoading: true });
        if (cancelled) return;

        hubConnection = new signalR.HubConnectionBuilder()
          .withUrl(`${baseUrl}/consultationHub`, {
            accessTokenFactory: () => localStorage.getItem("token"),
          })
          .withAutomaticReconnect()
          .build();

        hubConnection.on(
          "ReceiveMessage",
          (
            _sessionId,
            senderId,
            messageType,
            content,
            timestamp,
            messageId,
            replyToMessageId,
          ) => {
            const newMsg = formatIncomingMessageRef.current?.({
              id: messageId,
              senderId,
              messageType,
              content,
              timestamp,
              replyToMessageId,
            });
            if (!newMsg) return;

            setMessages((prev) => {
              if (prev.some((m) => m.id === newMsg.id)) return prev;
              if (
                senderId === userId &&
                prev.some(
                  (m) =>
                    String(m.id).startsWith("temp_") &&
                    m.senderId === userId &&
                    m.type === newMsg.type,
                )
              ) {
                return prev;
              }
              return [...prev, newMsg];
            });

            if (senderId !== userId) {
              const currentSession = sessionRef.current;
              const isParticipant =
                !!currentSession &&
                (currentSession.doctorId === userId ||
                  currentSession.patientId === userId ||
                  currentSession.DoctorId === userId ||
                  currentSession.PatientId === userId);
              if (isParticipant) {
                hubConnection
                  .invoke("MarkAsRead", messageId, userId)
                  .catch((error) => console.error("MarkAsRead Error: ", error));
              }
            }
          },
        );

        hubConnection.on("MessageEdited", (msgId, newContent) => {
          setMessages((msgs) =>
            msgs.map((m) =>
              m.id === msgId ? { ...m, text: newContent, isEdited: true } : m,
            ),
          );
        });

        hubConnection.on("MessageDeleted", (msgId) => {
          setMessages((msgs) =>
            msgs.filter((m) => String(m.id) !== String(msgId)),
          );
        });

        hubConnection.on("MessageRead", (messageId) => {
          setMessages((msgs) =>
            msgs.map((m) =>
              m.id === messageId && m.senderId === userId
                ? { ...m, status: "read" }
                : m,
            ),
          );
        });

        hubConnection.onreconnected(() => {
          hubConnection
            .invoke("JoinSession", sessionId)
            .catch((err) =>
              console.error("Failed to rejoin session after reconnect:", err),
            );
        });

        await hubConnection.start();
        await hubConnection.invoke("JoinSession", sessionId);
        if (cancelled) {
          await hubConnection.stop();
          return;
        }
        setConnection(hubConnection);
      } catch (error) {
        console.error("Initialization Error: ", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initializeChat();

    return () => {
      cancelled = true;
      setConnection(null);
      if (hubConnection) {
        hubConnection.off("ReceiveMessage");
        hubConnection.off("MessageEdited");
        hubConnection.off("MessageDeleted");
        hubConnection.off("MessageRead");
        hubConnection.stop().catch(() => {});
      }
    };
  }, [sessionId, userId, GetChatInfo]);

  useEffect(() => {
    if (loading || !session || !userId) return;
    if (!localStorage.getItem(infoModalStorageKey)) {
      setOpenInfoModal(true);
    }
  }, [sessionId, session, loading, userId, infoModalStorageKey]);

  useEffect(() => {
    if (loading || !hasPreviousMessages || !userId) return;
    if (!localStorage.getItem(historyHintStorageKey)) {
      setShowHistoryHint(true);
      localStorage.setItem(historyHintStorageKey, "true");
    }
  }, [loading, hasPreviousMessages, userId, historyHintStorageKey]);

  useEffect(() => {
    forceScrollRef.current = true;
    isPinnedToBottomRef.current = true;
    prevLastMessageIdRef.current = null;
    setShowPrevious(false);
    setPreviousGroups([]);
    setHasPreviousMessages(false);
    setPreviousError("");
  }, [sessionId]);

  useLayoutEffect(() => {
    if (loading || !messages.length) return;

    const lastId = messages[messages.length - 1]?.id;
    const isNewMessage = lastId !== prevLastMessageIdRef.current;
    prevLastMessageIdRef.current = lastId;

    if (forceScrollRef.current) {
      isPinnedToBottomRef.current = true;
      setShowJumpToLatest(false);
      scrollChatToBottom();
      const frame = requestAnimationFrame(() => {
        scrollChatToBottom();
        forceScrollRef.current = false;
      });
      return () => cancelAnimationFrame(frame);
    }

    if (isNewMessage && isPinnedToBottomRef.current) {
      scrollChatToBottom();
    }
  }, [loading, messages, scrollChatToBottom]);

  useLayoutEffect(() => {
    const target = pendingHistoryScrollRef.current;
    if (!target) return undefined;
    pendingHistoryScrollRef.current = null;
    const el = messagesContainerRef.current;
    if (!el) return undefined;

    if (target === "top") {
      isPinnedToBottomRef.current = false;
      el.scrollTop = 0;
      setShowJumpToLatest(true);
      return undefined;
    }

    isPinnedToBottomRef.current = true;
    scrollChatToBottom();
    setShowJumpToLatest(false);
    return undefined;
  }, [showPrevious, previousGroups, scrollChatToBottom]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    const inner = messagesInnerRef.current;
    if (!container || !inner || loading) return;

    const observer = new ResizeObserver(() => {
      if (!isPinnedToBottomRef.current) return;
      scrollingProgrammaticallyRef.current = true;
      container.scrollTop = container.scrollHeight;
      requestAnimationFrame(() => {
        scrollingProgrammaticallyRef.current = false;
      });
    });
    observer.observe(inner);
    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    const sentinel = bottomSentinelRef.current;
    if (!container || loading) return;

    const onScroll = () => syncJumpButton();
    container.addEventListener("scroll", onScroll, { passive: true });
    container.addEventListener("touchmove", onScroll, { passive: true });

    let io;
    if (sentinel && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        ([entry]) => {
          if (isPinnedToBottomRef.current) return;
          setShowJumpToLatest(!entry.isIntersecting);
        },
        { root: container, threshold: 0 },
      );
      io.observe(sentinel);
    }

    return () => {
      container.removeEventListener("scroll", onScroll);
      container.removeEventListener("touchmove", onScroll);
      io?.disconnect();
    };
  }, [loading, syncJumpButton]);

  const sendMessage = async () => {
    if (connection && newMessage.trim() !== "") {
      const tempId = `temp_${Date.now()}`;
      const optimisticMessage = {
        id: tempId,
        position: "right",
        title: "شما",
        type: "text",
        text: newMessage,
        date: new Date(),
        status: "waiting",
        senderId: userId,
        replyToMessageId: replyToMessageId,
      };

      forceScrollRef.current = true;
      isPinnedToBottomRef.current = true;
      setMessages((prev) => [...prev, optimisticMessage]);
      setNewMessage("");
      setReplyToMessageId(null);
      setReplyToMessage(null);
      setEditingMessage(null);
      setResetKey((k) => k + 1);
      try {
        const permanentMessageData = await connection.invoke(
          "SendMessage",
          sessionId,
          userId,
          "text",
          optimisticMessage.text,
          optimisticMessage.replyToMessageId,
        );
        const formatted = formatIncomingMessage(permanentMessageData);
        if (!formatted) {
          throw new Error("Could not format sent message");
        }
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId ? { ...formatted, status: "sent" } : msg,
          ),
        );
      } catch (error) {
        console.error("SendMessage Error: ", error);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId ? { ...msg, status: "failed" } : msg,
          ),
        );
      }
    }
  };

  const canEditMessage = (message) =>
    !message?.isPrevious &&
    !isChatEnded &&
    message?.position === "right" &&
    message?.type === "text" &&
    message.status !== "waiting" &&
    message.status !== "failed" &&
    message.id != null &&
    !String(message.id).startsWith("temp_");

  const startEditMessage = (message) => {
    if (!canEditMessage(message)) return;
    setReplyToMessage(null);
    setReplyToMessageId(null);
    setAttachOpen(false);
    setEditingMessage(message);
    setNewMessage(message.text || "");
    setActiveMessageId(null);
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    });
  };

  const cancelEditMessage = () => {
    setEditingMessage(null);
    setNewMessage("");
    setResetKey((k) => k + 1);
  };

  const saveEditedMessage = async () => {
    if (!connection || !editingMessage || isChatEnded) return;
    const next = newMessage.trim();
    if (!next) return;
    if (next === (editingMessage.text || "").trim()) {
      cancelEditMessage();
      return;
    }

    const messageId = editingMessage.id;
    try {
      await connection.invoke("EditMessage", messageId, next, sessionId);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, text: next, isEdited: true } : m,
        ),
      );
      cancelEditMessage();
    } catch (error) {
      console.error("EditMessage Error: ", error);
      alert("ویرایش پیام انجام نشد.");
    }
  };

  const retryFailedMessage = async (message) => {
    if (!connection || isChatEnded || message.status !== "failed") return;

    setMessages((prev) =>
      prev.map((m) => (m.id === message.id ? { ...m, status: "waiting" } : m)),
    );

    if (message.type === "text") {
      try {
        const permanentMessageData = await connection.invoke(
          "SendMessage",
          sessionId,
          userId,
          "text",
          message.text,
          message.replyToMessageId,
        );
        const formatted = formatIncomingMessage(permanentMessageData);
        if (!formatted) {
          throw new Error("Could not format retried message");
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === message.id ? { ...formatted, status: "sent" } : m,
          ),
        );
      } catch (error) {
        console.error("Retry SendMessage Error: ", error);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === message.id ? { ...m, status: "failed" } : m,
          ),
        );
      }
      return;
    }

    const retry = message.retryPayload;
    if (!retry?.fileOrBlob || !retry.uploadEndpoint) return;

    try {
      const formData = new FormData();
      const fileExtension = retry.fileOrBlob.name
        ? retry.fileOrBlob.name.split(".").pop()
        : "mp3";
      formData.append("file", retry.fileOrBlob, `upload.${fileExtension}`);

      const response = await fetch(`${baseUrl}${retry.uploadEndpoint}`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok)
        throw new Error(`Upload failed with status: ${response.status}`);

      const responseData = await response.json();
      const permanentUrl = responseData.imageUrl || responseData.fileUrl;
      const permanentMessageData = await connection.invoke(
        "SendMessage",
        sessionId,
        userId,
        message.type,
        permanentUrl,
        message.replyToMessageId,
      );
      const formatted = formatIncomingMessage(permanentMessageData);
      if (!formatted) {
        throw new Error("Could not format retried upload");
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === message.id ? { ...formatted, status: "sent" } : m,
        ),
      );
      if (retry.localUrl) URL.revokeObjectURL(retry.localUrl);
    } catch (error) {
      console.error("Retry upload error:", error);
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, status: "failed" } : m)),
      );
    }
  };

  const getReplyPreviewText = (m) => {
    if (!m) return "";
    if (m.type === "photo") return "📷 تصویر";
    if (m.type === "file") return "📄 فایل";
    if (m.type === "audio") return "🎤 پیام صوتی";
    return m.text || "";
  };

  const sameMessageId = (left, right) => String(left) === String(right);

  const toServerMessageId = (id) => {
    if (id == null) return null;
    const raw = String(id);
    if (raw.startsWith("temp_")) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : null;
  };

  const removeMessagesByIds = (ids) => {
    const idSet = new Set(ids.map(String));
    setMessages((prev) => {
      const next = [];
      for (const message of prev) {
        if (idSet.has(String(message.id))) {
          const localUrl =
            message.retryPayload?.localUrl ||
            (typeof message.photoUrl === "string" &&
            message.photoUrl.startsWith("blob:")
              ? message.photoUrl
              : null);
          if (localUrl) {
            try {
              URL.revokeObjectURL(localUrl);
            } catch {
              /* ignore */
            }
          }
          continue;
        }
        next.push(message);
      }
      return next;
    });
  };

  const deleteMessage = async () => {
    const ids = [...deleteMessageIds];
    if (ids.length === 0) return;

    const serverIds = ids.map(toServerMessageId).filter((id) => id != null);
    const localOnly = ids.filter((id) => toServerMessageId(id) == null);

    setOpenDeleteDialog(false);
    setDeleteMessageIds([]);
    setActiveMessageId(null);

    if (currentImageIndex != null) {
      const remaining = allImages.filter(
        (image) => !ids.some((id) => sameMessageId(image.id, id)),
      );
      if (remaining.length === 0) {
        setCurrentImageIndex(null);
      } else {
        setCurrentImageIndex(Math.min(currentImageIndex, remaining.length - 1));
      }
    }

    removeMessagesByIds(ids);

    if (localOnly.length === ids.length) return;
    if (!connection) return;

    try {
      for (const id of serverIds) {
        await connection.invoke("DeleteMessage", id);
      }
    } catch (error) {
      console.error("DeleteMessage Error: ", error);
    }
  };

  const askDeleteMessages = (ids) => {
    const uniqueIds = [...new Set(ids.filter((id) => id != null && id !== ""))];
    if (uniqueIds.length === 0) return;
    setDeleteMessageIds(uniqueIds);
    setOpenDeleteDialog(true);
    setActiveMessageId(null);
  };

  const handleBack = () => {
    navigate(from, { state: { status } });
  };

  const CloseSessionHandl = async () => {
    try {
      await CloseConsultation(sessionId);
      setOpenDialog(false);
      navigate(from);
    } catch (error) {
      console.error("Close Session Error: ", error);
    }
  };

  const ReopenSessionHandlr = async () => {
    try {
      await ReopenConsultation(sessionId);
      setOpenDialogForReopenConsultation(false);
      await GetChatInfo();
    } catch (error) {
      console.error("Close Session Error: ", error);
    }
  };

  const getDateKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const formatDayLabel = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (getDateKey(d) === getDateKey(today)) return "امروز";
    if (getDateKey(d) === getDateKey(yesterday)) return "دیروز";
    return d.toLocaleDateString("fa-IR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatMessageTime = (date) => {
    try {
      return new Date(date).toLocaleTimeString("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const handleComposerChange = (e) => {
    setNewMessage(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  };

  const formatConsultationDate = (date) => {
    try {
      return new Date(date).toLocaleDateString("fa-IR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  const togglePreviousMessages = async () => {
    setShowHistoryHint(false);
    if (previousLoading) return;

    if (showPrevious) {
      pendingHistoryScrollRef.current = "bottom";
      isPinnedToBottomRef.current = true;
      setShowPrevious(false);
      setPreviousError("");
      return;
    }

    setPreviousError("");
    if (previousGroups.length > 0) {
      pendingHistoryScrollRef.current = "top";
      isPinnedToBottomRef.current = false;
      setShowPrevious(true);
      return;
    }

    setPreviousLoading(true);
    try {
      const groups = await GetPreviousConsultationMessages(sessionId);
      const formatted = (groups || [])
        .map((group) => ({
          sessionId: group.sessionId ?? group.SessionId,
          startTime: group.startTime ?? group.StartTime,
          messages: (group.messages || group.Messages || [])
            .map((message) => formatStoredMessage(message, true))
            .filter(Boolean),
        }))
        .filter((group) => group.messages.length > 0);

      if (formatted.length === 0) {
        setHasPreviousMessages(false);
        return;
      }

      pendingHistoryScrollRef.current = "top";
      isPinnedToBottomRef.current = false;
      setPreviousGroups(formatted);
      setShowPrevious(true);
    } catch (error) {
      console.error("Error loading previous consultations:", error);
      setPreviousError("بارگذاری پیام‌های قبلی ممکن نشد. دوباره تلاش کنید.");
    } finally {
      setPreviousLoading(false);
    }
  };

  const jumpToLatest = () => {
    isPinnedToBottomRef.current = true;
    setShowJumpToLatest(false);
    scrollChatToBottom();
  };

  const counterpartName =
    userId !== session?.doctorId && userId === session?.patientId
      ? `دکتر ${session?.doctorName || ""}`.trim()
      : session?.patientName || "مشاوره";
  const isPatientView =
    userId !== session?.doctorId && userId === session?.patientId;
  const canAttach = canUploadImage || canUploadPdf || canUploadVoice;

  const PHOTO_GROUP_MS = 5 * 60 * 1000;
  const threadItems = [];
  const historyMessages = showPrevious
    ? previousGroups.flatMap((group) => group.messages || [])
    : [];
  const quotePool = [...historyMessages, ...messages];

  const appendThread = (list, keyPrefix) => {
    let lastDateKey = null;
    for (let i = 0; i < list.length; ) {
      const message = list[i];
      const dateKey = getDateKey(message.date);
      if (dateKey !== lastDateKey) {
        threadItems.push({
          kind: "day",
          id: `${keyPrefix}-day-${dateKey}`,
          label: formatDayLabel(message.date),
        });
        lastDateKey = dateKey;
      }

      if (message.type === "photo") {
        const album = [message];
        let j = i + 1;
        while (j < list.length) {
          const next = list[j];
          const prev = album[album.length - 1];
          if (next.type !== "photo") break;
          if (next.senderId !== message.senderId) break;
          if (getDateKey(next.date) !== dateKey) break;
          if (next.replyToMessageId) break;
          if (
            Math.abs(new Date(next.date) - new Date(prev.date)) > PHOTO_GROUP_MS
          ) {
            break;
          }
          album.push(next);
          j += 1;
        }
        threadItems.push({
          kind: "msg",
          id: `${keyPrefix}-${message.id}`,
          message: album[album.length - 1],
          album,
        });
        i = j;
        continue;
      }

      threadItems.push({
        kind: "msg",
        id: `${keyPrefix}-${message.id}`,
        message,
      });
      i += 1;
    }
  };

  if (showPrevious) {
    previousGroups.forEach((group) => {
      if (!group.messages?.length) return;
      const when = formatConsultationDate(group.startTime);
      threadItems.push({
        kind: "session",
        id: `prev-session-${group.sessionId}`,
        label: when ? `مشاوره قبلی · ${when}` : "مشاوره قبلی",
      });
      appendThread(group.messages, `prev-${group.sessionId}`);
    });
    if (threadItems.length > 0) {
      threadItems.push({
        kind: "session",
        id: "current-session",
        label: "مشاوره فعلی",
        current: true,
      });
    }
  }

  const currentMessages = messages.filter(Boolean);
  appendThread(currentMessages, "current");
  if (showPrevious && threadItems.some((item) => item.kind === "session") && currentMessages.length === 0) {
    threadItems.push({
      kind: "note",
      id: "current-empty",
      label: "پیامی در این مشاوره نیست",
    });
  }

  if (load || !content) {
    return <Loader />;
  }
  return (
    <>
      <div className="consultation-chat" dir="rtl" ref={chatRootRef}>
        <header className="chat-header">
          <button type="button" className="chat-icon-btn" onClick={handleBack}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>

          <div className="chat-header-main">
            <div className="chat-avatar">
              {isPatientView ? (
                <Avatar
                  src={resolveMediaUrl(content.doctorAvatarUrl)}
                  alt={counterpartName}
                  sx={{ width: 42, height: 42 }}
                />
              ) : (
                <div className="chat-avatar-fallback">
                  <AccountCircleIcon />
                </div>
              )}
              <span
                className={`chat-avatar-dot${isChatEnded ? " is-ended" : ""}`}
              />
            </div>
            <div className="chat-header-copy">
              <h1>{counterpartName}</h1>
              <p className={isChatEnded ? "" : "is-live"}>
                {isChatEnded ? "مشاوره پایان یافته" : "مشاوره فعال"}
              </p>
            </div>
          </div>

          <div className="chat-header-actions">
            {isDoctor &&
              (!isChatEnded ? (
                <button
                  type="button"
                  className="chat-header-action is-end"
                  onClick={handleFinishConsultation}
                  title="اتمام مشاوره"
                >
                  <FontAwesomeIcon icon={faStop} />
                  <span>اتمام مشاوره</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="chat-header-action is-reopen"
                  onClick={handleReopenConsultation}
                  title="باز کردن مجدد"
                >
                  <FontAwesomeIcon icon={faRotateRight} />
                  <span>باز کردن مجدد</span>
                </button>
              ))}
            {hasPreviousMessages && !loading && (
              <Tooltip
                title={
                  <div className="chat-history-hint" dir="rtl">
                    <span className="chat-history-hint-icon">
                      <History fontSize="small" />
                    </span>
                    <span className="chat-history-hint-copy">
                      <strong>تاریخچهٔ گفتگوها</strong>
                      <small>برای دیدن پیام‌های قبلی، اینجا بزنید.</small>
                    </span>
                    <button
                      type="button"
                      className="chat-history-hint-close"
                      aria-label="بستن راهنما"
                      onClick={() => setShowHistoryHint(false)}
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  </div>
                }
                placement="bottom"
                arrow
                open={showHistoryHint}
                onClose={() => setShowHistoryHint(false)}
                disableHoverListener
                disableFocusListener
                disableTouchListener
                classes={{ popper: "chat-history-tooltip" }}
              >
                <IconButton
                  type="button"
                  className="chat-header-history"
                  aria-label={
                    showPrevious
                      ? "بستن پیام‌های مشاوره‌های قبلی"
                      : "مشاهده پیام‌های مشاوره‌های قبلی"
                  }
                  title={
                    showPrevious
                      ? "بستن پیام‌های قبلی"
                      : "مشاهده پیام‌های قبلی"
                  }
                  onClick={togglePreviousMessages}
                  disabled={previousLoading}
                  size="small"
                >
                  <History />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </header>

        <div className="chat-body">
          {previousError && !loading && (
            <p className="chat-history-error">{previousError}</p>
          )}
          <div
            ref={messagesContainerRef}
            className="chat-thread"
            onScroll={handleMessagesScroll}
            onClick={() => setActiveMessageId(null)}
          >
            {loading ? (
              <Loader />
            ) : (
              <div ref={messagesInnerRef} className="chat-thread-inner">
                {threadItems.length === 0 ? (
                  <div className="chat-empty">
                    <div className="chat-empty-mark">💬</div>
                    <h2>
                      {session
                        ? isChatEnded
                          ? "پیامی در این مشاوره وجود ندارد"
                          : isDoctor
                            ? "شروع گفتگو با بیمار"
                            : "شروع گفتگو با پزشک"
                        : "صفحه چت یافت نشد"}
                    </h2>
                    <p>
                      {session && !isChatEnded
                        ? "پیام، تصویر یا فایل خود را از پایین صفحه بفرستید."
                        : ""}
                    </p>
                  </div>
                ) : (
                  threadItems.map((item) => {
                    if (item.kind === "session") {
                      return (
                        <div
                          key={item.id}
                          className={`chat-session-mark${
                            item.current ? " is-current" : ""
                          }`}
                        >
                          {item.label}
                        </div>
                      );
                    }

                    if (item.kind === "note") {
                      return (
                        <div key={item.id} className="chat-history-note">
                          {item.label}
                        </div>
                      );
                    }

                    if (item.kind === "day") {
                      return (
                        <div key={item.id} className="chat-day">
                          {item.label}
                        </div>
                      );
                    }

                    const message = item.message;
                    const album = item.album || [message];
                    const isAlbum = album.length > 1;
                    const isOwn = message.position === "right";
                    const quotedSource = album.find((m) => m.replyToMessageId);
                    const quoted = quotedSource?.replyToMessageId
                      ? quotePool.find(
                          (m) => m.id === quotedSource.replyToMessageId,
                        )
                      : null;
                    const albumStatus = album.some((m) => m.status === "failed")
                      ? "failed"
                      : album.some((m) => m.status === "waiting")
                        ? "waiting"
                        : album.every((m) => m.status === "read")
                          ? "read"
                          : album.some((m) => m.status === "sent")
                            ? "sent"
                            : message.status;
                    // WhatsApp-style collage: at most four tiles, the rest fold
                    // into a "+N" overlay on the last one.
                    const albumTiles = album.slice(0, 4);
                    const hiddenPhotos = album.length - albumTiles.length;
                    const albumCountClass = `count-${albumTiles.length}`;

                    return (
                      <div
                        key={item.id}
                        ref={(el) => {
                          album.forEach((m) => {
                            if (el) messageRefs.current[m.id] = el;
                            else delete messageRefs.current[m.id];
                          });
                        }}
                        className={`chat-row ${isOwn ? "is-own" : "is-their"}${
                          message.isPrevious ? " is-previous" : ""
                        }${
                          message.type === "audio" ? " is-audio" : ""
                        }${isAlbum ? " is-album" : ""}${
                          album.some((m) => m.id === highlightedId)
                            ? " is-flash"
                            : ""
                        }${
                          editingMessage?.id === message.id ? " is-editing" : ""
                        }`}
                      >
                        {!isOwn && (
                          <div className="chat-sender">{message.title}</div>
                        )}
                        <div
                          className={`chat-bubble${isAlbum ? " is-album" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (message.isPrevious) return;
                            if (
                              e.target.closest(
                                "a, .chat-photo, .chat-album-item, audio, .chat-quote, .chat-audio-player",
                              )
                            ) {
                              return;
                            }
                            setActiveMessageId((prev) =>
                              prev === message.id ? null : message.id,
                            );
                          }}
                        >
                          {quoted && (
                            <button
                              type="button"
                              className="chat-quote"
                              onClick={() =>
                                scrollToMessage(quotedSource.replyToMessageId)
                              }
                            >
                              <strong>{quoted.title}</strong>
                              <span>{getReplyPreviewText(quoted)}</span>
                            </button>
                          )}

                          {album[0].type === "photo" ? (
                            isAlbum ? (
                              <div className={`chat-album ${albumCountClass}`}>
                                {albumTiles.map((photo, index) => (
                                  <button
                                    key={photo.id}
                                    type="button"
                                    className="chat-album-item"
                                    onClick={() =>
                                      openFullScreenImage(photo.id)
                                    }
                                  >
                                    <img
                                      src={photo.photoUrl}
                                      alt="تصویر ارسالی"
                                    />
                                    {hiddenPhotos > 0 &&
                                      index === albumTiles.length - 1 && (
                                        <span className="chat-album-more">
                                          +
                                          {new Intl.NumberFormat(
                                            "fa-IR",
                                          ).format(hiddenPhotos)}
                                        </span>
                                      )}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="chat-photo"
                                onClick={() => openFullScreenImage(message.id)}
                              >
                                <img
                                  src={message.photoUrl}
                                  alt="تصویر ارسالی"
                                />
                              </button>
                            )
                          ) : message.type === "file" ? (
                            <a
                              className="chat-file"
                              href={message.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => openInNewTab(message.fileUrl, e)}
                            >
                              <span className="chat-file-icon">
                                <FontAwesomeIcon icon={faFilePdf} />
                              </span>
                              <span>
                                <strong>فایل پیوست</strong>
                                <small>PDF</small>
                              </span>
                            </a>
                          ) : message.type === "audio" ? (
                            <ChatAudioPlayer src={message.audioUrl} />
                          ) : (
                            <p className="chat-text">
                              {renderLinkedText(message.text)}
                            </p>
                          )}

                          <div className="chat-meta">
                            {message.isEdited && (
                              <span className="chat-edited">ویرایش‌شده</span>
                            )}
                            <span>{formatMessageTime(message.date)}</span>
                            {isOwn && albumStatus === "waiting" && (
                              <Schedule className="chat-tick chat-tick--wait" />
                            )}
                            {isOwn && albumStatus === "sent" && (
                              <DoneAll className="chat-tick chat-tick--sent" />
                            )}
                            {isOwn && albumStatus === "read" && (
                              <DoneAll className="chat-tick chat-tick--read" />
                            )}
                          </div>
                        </div>

                        {activeMessageId === message.id && (
                          <div className="chat-actions">
                            {!isChatEnded && !message.isPrevious && (
                              <button
                                type="button"
                                className="chat-action"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cancelEditMessage();
                                  setReplyToMessageId(message.id);
                                  setReplyToMessage(message);
                                  setActiveMessageId(null);
                                }}
                              >
                                <Reply fontSize="small" />
                                پاسخ
                              </button>
                            )}
                            {canEditMessage(message) && (
                              <button
                                type="button"
                                className="chat-action"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditMessage(message);
                                }}
                              >
                                <EditOutlined fontSize="small" />
                                ویرایش
                              </button>
                            )}
                            {message.showRemoveButton && !isAlbum && (
                              <button
                                type="button"
                                className="chat-action is-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  askDeleteMessages([message.id]);
                                }}
                              >
                                <DeleteOutline fontSize="small" />
                                حذف
                              </button>
                            )}
                            {isAlbum &&
                              album.some((m) => m.showRemoveButton) && (
                                <button
                                  type="button"
                                  className="chat-action is-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    askDeleteMessages(
                                      album
                                        .filter((m) => m.showRemoveButton)
                                        .map((m) => m.id),
                                    );
                                  }}
                                >
                                  <DeleteSweep fontSize="small" />
                                  حذف همه
                                </button>
                              )}
                          </div>
                        )}

                        {album
                          .filter((m) => m.status === "failed")
                          .map((failed) => (
                            <button
                              key={failed.id}
                              type="button"
                              className="chat-retry"
                              onClick={() => retryFailedMessage(failed)}
                              disabled={isChatEnded}
                            >
                              <FontAwesomeIcon icon={faRotateRight} />
                              ارسال نشد · تلاش مجدد
                            </button>
                          ))}
                      </div>
                    );
                  })
                )}
                <div
                  ref={bottomSentinelRef}
                  className="chat-bottom-sentinel"
                  aria-hidden
                />
              </div>
            )}
          </div>

          <button
            type="button"
            className={`chat-jump${showJumpToLatest ? " is-on" : ""}`}
            onClick={jumpToLatest}
            aria-hidden={!showJumpToLatest}
            tabIndex={showJumpToLatest ? 0 : -1}
          >
            <KeyboardArrowDown />
          </button>
        </div>

        <div className="chat-dock">
          {isChatEnded ? (
            <div className="chat-ended chat-ended--feedback" dir="rtl">
              <div className="chat-ended-copy">
                <strong>مشاوره پایان یافته است</strong>
                {isPatientView && <span>از تجربه‌تان با پزشک بنویسید.</span>}
              </div>
              {isPatientView && <ReviewAction onClick={() => navigate(`/counseling/chat/index?review=${sessionId}`)} />}
            </div>
          ) : (
            <>
              {editingMessage && (
                <div className="chat-reply-bar is-edit">
                  <div>
                    <strong>ویرایش پیام</strong>
                    <span>{editingMessage.text}</span>
                  </div>
                  <button
                    type="button"
                    className="chat-icon-btn"
                    onClick={cancelEditMessage}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              )}

              {replyToMessage && !editingMessage && (
                <div className="chat-reply-bar">
                  <div>
                    <strong>{replyToMessage.title}</strong>
                    <span>{getReplyPreviewText(replyToMessage)}</span>
                  </div>
                  <button
                    type="button"
                    className="chat-icon-btn"
                    onClick={() => {
                      setReplyToMessage(null);
                      setReplyToMessageId(null);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              )}

              {attachOpen && (
                <div className="chat-attach-sheet">
                  {canUploadImage && (
                    <button
                      type="button"
                      className="chat-attach-item"
                      onClick={onImageSelect}
                    >
                      <span className="chat-attach-icon">
                        <FontAwesomeIcon icon={faCamera} />
                      </span>
                      <span>تصویر</span>
                    </button>
                  )}
                  {canUploadPdf && (
                    <button
                      type="button"
                      className="chat-attach-item"
                      onClick={onFileSelect}
                    >
                      <span className="chat-attach-icon is-green">
                        <FontAwesomeIcon icon={faFilePdf} />
                      </span>
                      <span>فایل PDF</span>
                    </button>
                  )}
                  {canUploadVoice && (
                    <button
                      type="button"
                      className="chat-attach-item"
                      onClick={onVoiceSelect}
                    >
                      <span
                        className={`chat-attach-icon${
                          recording ? " is-red" : ""
                        }`}
                      >
                        <FontAwesomeIcon icon={faMicrophone} />
                      </span>
                      <span>{recording ? "توقف ضبط" : "ضبط صوت"}</span>
                    </button>
                  )}
                  {!canAttach && isPatient && (
                    <div className="chat-attach-item">
                      <span>امکان ارسال فایل غیرفعال است</span>
                    </div>
                  )}
                </div>
              )}

              <div className="chat-composer">
                {!editingMessage && (
                  <button
                    type="button"
                    className="chat-icon-btn"
                    onClick={handleAttachClick}
                    disabled={!canAttach}
                  >
                    <FontAwesomeIcon icon={faPaperclip} />
                  </button>
                )}

                {recording ? (
                  <div className="chat-recording">
                    <span className="chat-recording-dot" />
                    در حال ضبط صدا...
                  </div>
                ) : (
                  <textarea
                    key={resetKey}
                    ref={composerRef}
                    rows={1}
                    placeholder={
                      editingMessage
                        ? "متن جدید را بنویسید..."
                        : "پیام خود را بنویسید..."
                    }
                    value={newMessage}
                    onChange={handleComposerChange}
                  />
                )}

                {editingMessage ? (
                  <button
                    type="button"
                    className="chat-send"
                    onClick={saveEditedMessage}
                    disabled={!newMessage.trim()}
                  >
                    <Check fontSize="small" />
                  </button>
                ) : recording || (!newMessage.trim() && canUploadVoice) ? (
                  <button
                    type="button"
                    className={`chat-mic${recording ? " is-recording" : ""}`}
                    onClick={onVoiceSelect}
                  >
                    <FontAwesomeIcon icon={faMicrophone} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="chat-send"
                    onClick={sendMessage}
                    disabled={!newMessage.trim()}
                  >
                    <Send fontSize="small" className="chat-send-arrow" />
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <input
          type="file"
          accept=".jpg,.jpeg,.png"
          ref={imageInputRef}
          style={{ display: "none" }}
          multiple
          onChange={handleImageUpload}
        />
        <input
          type="file"
          accept=".pdf"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />
      </div>

      <Dialog
        fullScreen
        open={currentImageIndex !== null}
        onClose={closeGallery}
        disableEnforceFocus={openDeleteDialog}
        disableAutoFocus={openDeleteDialog}
      >
        <IconButton
          onClick={closeGallery}
          aria-label="بستن"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1300,
            color: "white",
          }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>

        {currentImageDownload && (
          <a
            className="chat-gallery-download"
            href={currentImageDownload.href}
            download={
              currentImageDownload.direct ? currentImageDownload.fileName : undefined
            }
            aria-label="دانلود"
          >
            <FileDownloadOutlined fontSize="large" />
          </a>
        )}

        {allImages[currentImageIndex]?.showRemoveButton && !isChatEnded && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              askDeleteMessages([allImages[currentImageIndex].id]);
            }}
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              zIndex: 1300,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.35)",
            }}
          >
            <DeleteOutline />
          </IconButton>
        )}

        {allImages.length > 1 && (
          <div className="chat-gallery-film">
            {(currentImageIndex ?? 0) + 1} / {allImages.length}
          </div>
        )}

        {allImages.length > 1 && (
          <IconButton
            disabled={currentImageIndex === 0}
            onClick={handlePrevImage}
            sx={{
              position: "absolute",
              top: "50%",
              left: 16,
              zIndex: 1300,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.3)",
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        )}

        {allImages.length > 1 && (
          <IconButton
            disabled={currentImageIndex === allImages.length - 1}
            onClick={handleNextImage}
            sx={{
              position: "absolute",
              top: "50%",
              right: 16,
              zIndex: 1300,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.3)",
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}

        <DialogContent
          className="chat-gallery-content"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "black",
            padding: 0,
            overflow: "hidden",
            userSelect: "none",
            height: "100%",
          }}
        >
          <ChatZoomableImage
            src={
              currentImageIndex !== null
                ? allImages[currentImageIndex]?.photoUrl
                : ""
            }
            allowSwipe={allImages.length > 1}
            onSwipe={handleGallerySwipe}
          />
        </DialogContent>
      </Dialog>

      <ConfirmModal
        openDialog={openDialogForReopenConsultation}
        handleCloseDialog={() => setOpenDialogForReopenConsultation(false)}
        handleConfirm={ReopenSessionHandlr}
        text={["آیا از باز کردن مجدد مشاوره مطمئن هستید؟"]}
        cancleText="خیر"
        confirmText="بله"
        title="باز کردن مجدد مشاوره"
      />
      <ConfirmModal
        openDialog={openDialog}
        handleCloseDialog={() => setOpenDialog(false)}
        handleConfirm={CloseSessionHandl}
        text={["آیا از اتمام مشاوره مطمئن هستید؟"]}
        cancleText="خیر"
        confirmText="بله"
        title="اتمام مشاوره"
      />
      <ConfirmModal
        openDialog={openDeleteDialog}
        handleCloseDialog={() => {
          setOpenDeleteDialog(false);
          setDeleteMessageIds([]);
        }}
        handleConfirm={deleteMessage}
        text={
          deleteMessageIds.length > 1
            ? ["همه تصاویر این آلبوم حذف می‌شوند. مطمئنی؟"]
            : ["آیا از حذف این پیام اطمینان دارید؟"]
        }
        cancleText="خیر"
        confirmText="بله"
        title={deleteMessageIds.length > 1 ? "حذف آلبوم" : "حذف پیام"}
      />
      <InfoModal
        handleCloseDialog={handleInfoModalClose}
        openDialog={openInfoModal}
        title={"راهنمای استفاده از چت"}
        text={[
          "برای پاسخ، ویرایش یا حذف، یک بار روی پیام بزنید تا دکمه‌ها ظاهر شوند.",
          "لطفا برای تجربه بهتر، حتما vpn خود را خاموش کنید.",
        ]}
        confirnButtonText={"متوجه شدم"}
      />
    </>
  );
};

export default ChatComponent;
